import {
  API_CACHE_NAME,
  PURGE_ACK_TIMEOUT_MS,
  purgeServiceWorkerCache,
  type CachePurgeOutcome,
} from '@/session/teardown';
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import { keepMailOnDevice } from './keepOnDevice';

/**
 * The *other* copy of the user's mail on this disk, and what the setting does
 * about it (ADR 0060 §3).
 *
 * ADR 0059's IndexedDB cache is the one this module's neighbours manage. It is
 * not the only one: the service worker keeps `ols-api-cache`, a NetworkFirst
 * cache of up to 200 API reads for a day (vite.config.ts), and that is mail
 * too — folder listings, message lists, message bodies. "Keep mail on this
 * device", off, that deleted one and left the other would be a setting that
 * lies, so this is the half of it that speaks to the worker.
 *
 * Two directions, both needed:
 *
 * - **Stop it writing.** {@link announceKeepMail} sends `ols:keep-mail`, which
 *   the worker records and the Workbox route reads. The flag does not survive
 *   the browser stopping an idle worker, so it is re-announced on every start
 *   and on every `controllerchange`.
 * - **Delete what it has.** {@link purgeApiCache} is the existing sign-out
 *   round trip (`ols:purge-cache`), reused verbatim — the caches belong to the
 *   worker and a page cannot delete them itself. It runs when the setting is
 *   turned off *and* on every start while it is off, which is what closes the
 *   window left by a worker that had not been told yet.
 */

export type { CachePurgeOutcome };

/** Tell the worker which way the setting is set. Fire and forget. */
export function announceKeepMail(keep: boolean = keepMailOnDevice()): void {
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'ols:keep-mail', keep });
  } catch {
    // No worker, no controller yet, or messaging refused. The purge below is
    // the backstop, and the worker's own default is the shipped behaviour.
  }
}

/**
 * Tell the worker a session is live, which lifts the "signed out" stop that
 * `ols:purge-cache` puts on its API cache (#35). Fire and forget.
 *
 * Sign-out's purge closes the cache whether or not the delete worked; this is
 * the only thing that reopens it, so it is sent only from a page that knows
 * it is signed in.
 */
export function announceSignedIn(): void {
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'ols:session', signedIn: true });
  } catch {
    // No worker or no controller: nothing has closed a cache that needs
    // reopening.
  }
}

/**
 * Announce the session to the worker on boot and after every sign-in, by
 * watching the `['session']` query: whenever it starts holding an account it
 * did not hold a moment ago. A refetch of the same session sends nothing.
 *
 * Returns the teardown.
 */
export function watchSessionForServiceWorker(client: QueryClient): () => void {
  let announcedFor: string | undefined;
  const check = (): void => {
    const session = client.getQueryData(queryKeys.session()) as { account?: { id?: string } } | undefined;
    const id = session?.account?.id;
    if (id !== undefined && id !== announcedFor) announceSignedIn();
    announcedFor = id;
  };
  check();
  return client.getQueryCache().subscribe((event) => {
    if (event.query.queryKey[0] === 'session' && event.query.queryKey.length === 1) check();
  });
}

/**
 * Keep telling it: a worker that restarts forgets, and a page that has just
 * taken a new controller has never told this one anything.
 *
 * Returns the teardown, so the provider's effect can stop listening.
 */
export function watchKeepMailForServiceWorker(read: () => boolean = keepMailOnDevice): () => void {
  const announce = (): void => announceKeepMail(read());
  announce();
  let container: ServiceWorkerContainer | undefined;
  try {
    container = navigator.serviceWorker;
  } catch {
    return () => undefined;
  }
  if (container === undefined) return () => undefined;
  container.addEventListener('controllerchange', announce);
  // `ready` resolves once there is an active registration; the controller can
  // arrive after the first paint on a first visit.
  void container.ready.then(announce).catch(() => undefined);
  return () => container.removeEventListener('controllerchange', announce);
}

/** Delete the worker's cache of API reads. Reports honestly, never assumes. */
export function purgeApiCache(timeoutMs: number = PURGE_ACK_TIMEOUT_MS): Promise<CachePurgeOutcome> {
  return purgeServiceWorkerCache(timeoutMs);
}

export { API_CACHE_NAME };
